import { useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { fabric } from 'fabric'
import { useEditorStore, Layer } from '@/stores/editorStore'

interface FabricCanvasProps {
  width: number
  height: number
  backgroundColor: string
  zoom: number
}

export interface FabricCanvasRef {
  exportToDataURL: (format: 'png' | 'jpeg', quality?: number) => string | null
}

const FabricCanvas = forwardRef<FabricCanvasRef, FabricCanvasProps>(({ width, height, backgroundColor, zoom }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fabricRef = useRef<fabric.Canvas | null>(null)
  const isUpdatingFromStore = useRef(false)

  const {
    cover,
    selectedLayerId,
    setSelectedLayer,
    updateLayer,
    tool,
  } = useEditorStore()

  // Initialize Fabric canvas
  useEffect(() => {
    if (!canvasRef.current) return

    const canvas = new fabric.Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor,
      selection: true,
      preserveObjectStacking: true,
    })

    fabricRef.current = canvas

    // Object selection event
    canvas.on('selection:created', (e) => {
      if (isUpdatingFromStore.current) return
      const obj = e.selected?.[0]
      if (obj?.data?.layerId) {
        setSelectedLayer(obj.data.layerId)
      }
    })

    canvas.on('selection:updated', (e) => {
      if (isUpdatingFromStore.current) return
      const obj = e.selected?.[0]
      if (obj?.data?.layerId) {
        setSelectedLayer(obj.data.layerId)
      }
    })

    canvas.on('selection:cleared', () => {
      if (isUpdatingFromStore.current) return
      setSelectedLayer(null)
    })

    // Object modified event (move, scale, rotate)
    canvas.on('object:modified', (e) => {
      if (isUpdatingFromStore.current) return
      const obj = e.target
      if (!obj?.data?.layerId) return

      const updates: Partial<Layer> = {
        x: Math.round(obj.left || 0),
        y: Math.round(obj.top || 0),
        width: Math.round((obj.width || 0) * (obj.scaleX || 1)),
        height: Math.round((obj.height || 0) * (obj.scaleY || 1)),
        rotation: Math.round(obj.angle || 0),
      }

      updateLayer(obj.data.layerId, updates)
    })

    // Text editing
    canvas.on('text:changed', (e) => {
      if (isUpdatingFromStore.current) return
      const obj = e.target as fabric.IText
      if (!obj?.data?.layerId) return
      updateLayer(obj.data.layerId, { content: obj.text || '' })
    })

    return () => {
      canvas.dispose()
      fabricRef.current = null
    }
  }, [])

  // Update canvas size
  useEffect(() => {
    if (!fabricRef.current) return
    fabricRef.current.setWidth(width)
    fabricRef.current.setHeight(height)
    fabricRef.current.setBackgroundColor(backgroundColor, () => {
      fabricRef.current?.renderAll()
    })
  }, [width, height, backgroundColor])

  // Sync layers from store to canvas
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas || !cover) return

    isUpdatingFromStore.current = true

    // Get current object IDs on canvas
    const canvasObjects = canvas.getObjects()
    const storeLayerIds = new Set(cover.layers.map(l => l.id))

    // Remove objects that are no longer in store
    canvasObjects.forEach(obj => {
      if (obj.data?.layerId && !storeLayerIds.has(obj.data.layerId)) {
        canvas.remove(obj)
      }
    })

    // Add or update layers
    cover.layers.forEach((layer, index) => {
      const existingObj = canvasObjects.find(obj => obj.data?.layerId === layer.id)

      if (existingObj) {
        // Update existing object
        updateFabricObject(existingObj, layer, index)
      } else {
        // Create new object
        createFabricObject(canvas, layer, index)
      }
    })

    // Reorder objects based on layer order
    cover.layers.forEach((layer, index) => {
      const obj = canvas.getObjects().find(o => o.data?.layerId === layer.id)
      if (obj) {
        canvas.moveTo(obj, index)
      }
    })

    canvas.renderAll()
    isUpdatingFromStore.current = false
  }, [cover?.layers])

  // Update selection when selectedLayerId changes
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return

    isUpdatingFromStore.current = true

    if (selectedLayerId) {
      const obj = canvas.getObjects().find(o => o.data?.layerId === selectedLayerId)
      if (obj) {
        canvas.setActiveObject(obj)
      }
    } else {
      canvas.discardActiveObject()
    }

    canvas.renderAll()
    isUpdatingFromStore.current = false
  }, [selectedLayerId])

  // Update cursor based on tool
  useEffect(() => {
    const canvas = fabricRef.current
    if (!canvas) return

    switch (tool) {
      case 'select':
        canvas.defaultCursor = 'default'
        canvas.hoverCursor = 'move'
        canvas.selection = true
        break
      case 'move':
        canvas.defaultCursor = 'grab'
        canvas.hoverCursor = 'grab'
        canvas.selection = false
        break
      case 'text':
        canvas.defaultCursor = 'text'
        canvas.hoverCursor = 'text'
        canvas.selection = true
        break
    }
  }, [tool])

  // Handle click for text tool
  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    const canvas = fabricRef.current
    if (!canvas || tool !== 'text') return

    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    const x = (e.clientX - rect.left) / zoom
    const y = (e.clientY - rect.top) / zoom

    // Check if clicked on existing object
    const target = canvas.findTarget(e.nativeEvent as MouseEvent, false)
    if (target) return

    // Create new text at click position
    const { addLayer } = useEditorStore.getState()
    addLayer({
      type: 'text',
      name: '텍스트',
      visible: true,
      locked: false,
      x: Math.round(x),
      y: Math.round(y),
      width: 200,
      height: 50,
      rotation: 0,
      opacity: 1,
      content: '텍스트 입력',
      fontFamily: 'Noto Sans KR',
      fontSize: 32,
      fontColor: '#ffffff',
      fontWeight: 400,
      textAlign: 'center',
    })
  }, [tool, zoom])

  // Expose export method
  useImperativeHandle(ref, () => ({
    exportToDataURL: (format: 'png' | 'jpeg', quality = 1) => {
      const canvas = fabricRef.current
      if (!canvas) return null

      // Save current active object
      const activeObject = canvas.getActiveObject()

      // Deselect all objects to hide selection controls
      canvas.discardActiveObject()
      canvas.renderAll()

      // Export canvas
      const dataUrl = canvas.toDataURL({
        format: format,
        quality: quality,
        multiplier: 1
      })

      // Restore selection if there was one
      if (activeObject) {
        canvas.setActiveObject(activeObject)
        canvas.renderAll()
      }

      return dataUrl
    }
  }))

  return (
    <div
      style={{
        transform: `scale(${zoom})`,
        transformOrigin: 'center center',
      }}
      onClick={handleCanvasClick}
    >
      <canvas ref={canvasRef} />
    </div>
  )
})

FabricCanvas.displayName = 'FabricCanvas'

export default FabricCanvas

// Helper: Update existing Fabric object
function updateFabricObject(obj: fabric.Object, layer: Layer, _index: number) {
  obj.set({
    left: layer.x,
    top: layer.y,
    angle: layer.rotation,
    opacity: layer.opacity,
    visible: layer.visible,
    selectable: !layer.locked,
    evented: !layer.locked,
  })

  if (layer.type === 'text' && obj instanceof fabric.IText) {
    obj.set({
      text: layer.content || '',
      fontFamily: layer.fontFamily || 'Noto Sans KR',
      fontSize: layer.fontSize || 32,
      fill: layer.fontColor || '#ffffff',
      fontWeight: layer.fontWeight || 400,
      textAlign: layer.textAlign || 'center',
    })

    if (layer.stroke) {
      obj.set({
        stroke: layer.stroke.color,
        strokeWidth: layer.stroke.width,
      })
    }

    if (layer.shadow) {
      obj.set({
        shadow: new fabric.Shadow({
          color: layer.shadow.color,
          blur: layer.shadow.blur,
          offsetX: layer.shadow.offsetX,
          offsetY: layer.shadow.offsetY,
        }),
      })
    }
  }

  obj.setCoords()
}

// Helper: Create new Fabric object
function createFabricObject(canvas: fabric.Canvas, layer: Layer, index: number) {
  if (layer.type === 'image' && layer.imageUrl) {
    fabric.Image.fromURL(layer.imageUrl, (img) => {
      img.set({
        left: layer.x,
        top: layer.y,
        scaleX: layer.width / (img.width || 1),
        scaleY: layer.height / (img.height || 1),
        angle: layer.rotation,
        opacity: layer.opacity,
        visible: layer.visible,
        selectable: !layer.locked,
        evented: !layer.locked,
        data: { layerId: layer.id },
      })
      canvas.add(img)
      canvas.moveTo(img, index)
      canvas.renderAll()
    }, { crossOrigin: 'anonymous' })
  } else if (layer.type === 'text') {
    const text = new fabric.IText(layer.content || '텍스트', {
      left: layer.x,
      top: layer.y,
      fontFamily: layer.fontFamily || 'Noto Sans KR',
      fontSize: layer.fontSize || 32,
      fill: layer.fontColor || '#ffffff',
      fontWeight: layer.fontWeight || 400,
      textAlign: layer.textAlign || 'center',
      angle: layer.rotation,
      opacity: layer.opacity,
      visible: layer.visible,
      selectable: !layer.locked,
      evented: !layer.locked,
      data: { layerId: layer.id },
    })

    if (layer.stroke) {
      text.set({
        stroke: layer.stroke.color,
        strokeWidth: layer.stroke.width,
      })
    }

    if (layer.shadow) {
      text.set({
        shadow: new fabric.Shadow({
          color: layer.shadow.color,
          blur: layer.shadow.blur,
          offsetX: layer.shadow.offsetX,
          offsetY: layer.shadow.offsetY,
        }),
      })
    }

    canvas.add(text)
    canvas.moveTo(text, index)
  } else if (layer.type === 'image' && !layer.imageUrl) {
    // Placeholder for image without URL
    const rect = new fabric.Rect({
      left: layer.x,
      top: layer.y,
      width: layer.width,
      height: layer.height,
      fill: '#333333',
      stroke: '#555555',
      strokeWidth: 1,
      angle: layer.rotation,
      opacity: layer.opacity,
      visible: layer.visible,
      selectable: !layer.locked,
      evented: !layer.locked,
      data: { layerId: layer.id },
    })
    canvas.add(rect)
    canvas.moveTo(rect, index)
  }
}

// Export canvas as image (utility function, can be called externally)
export function exportCanvasUtil(canvasElement: HTMLCanvasElement, format: 'png' | 'jpeg' = 'png', quality = 1): string {
  return canvasElement.toDataURL(`image/${format}`, quality)
}
